import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from './client'
import { useAuthStore } from '../store/authStore'
import { queryKeys } from '../lib/query'

// Types
interface User {
  id: string
  email: string
  username: string
  role: string
  telegramId?: number
  telegramUsername?: string
  createdAt?: string
  updatedAt?: string
}

interface LoginResponse {
  accessToken: string
  refreshToken: string
  user: User
}

interface RefreshResponse {
  accessToken: string
  refreshToken: string
}

interface LoginCredentials {
  email: string
  password: string
}

// Login mutation
export function useLogin() {
  const { setTokens, setUser } = useAuthStore()

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      return api.post<LoginResponse>('/auth/login', credentials, { skipAuth: true })
    },
    onSuccess: (data) => {
      setTokens(data.accessToken, data.refreshToken)
      setUser(data.user)
    },
  })
}

// Logout mutation
export function useLogout() {
  const { refreshToken, logout } = useAuthStore()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (refreshToken) {
        try {
          await api.post('/auth/logout', { refreshToken })
        } catch {
          // Ignore errors on logout
        }
      }
    },
    onSettled: () => {
      logout()
      queryClient.clear()
    },
  })
}

// Get current user query
export function useMe() {
  const { isAuthenticated } = useAuthStore()

  return useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: () => api.get<User>('/auth/me'),
    enabled: isAuthenticated,
    retry: false,
  })
}

// Telegram login mutation
export function useTelegramLogin() {
  const { setTokens, setUser } = useAuthStore()

  return useMutation({
    mutationFn: async (telegramData: Record<string, string | number>) => {
      return api.post<LoginResponse>('/auth/telegram', telegramData, { skipAuth: true })
    },
    onSuccess: (data) => {
      setTokens(data.accessToken, data.refreshToken)
      setUser(data.user)
    },
  })
}

// Refresh token mutation (for manual refresh if needed)
export function useRefreshToken() {
  const { refreshToken, setTokens, logout } = useAuthStore()

  return useMutation({
    mutationFn: async () => {
      if (!refreshToken) throw new ApiError(401, 'No refresh token')
      return api.post<RefreshResponse>('/auth/refresh', { refreshToken }, { skipAuth: true })
    },
    onSuccess: (data) => {
      setTokens(data.accessToken, data.refreshToken)
    },
    onError: () => {
      logout()
    },
  })
}
